import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { Response } from "../types";

import {
  filterSupportedEntities,
  ListFolderEntitiesRequest,
  ListFolderEntitiesResponse,
} from "./types";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";

export const useListFolderEntities = ({
  filter_types,
  folder_id,
  flatten,
  search_query,
  semantic_search,
  semantic_search_threshold,
  semantic_search_top_k,
  user_id,
  tags,
  enabled = true,
  keepPreviousData = true,
  include_metadata = false,
  include_previews = false,
}: ListFolderEntitiesRequest & {
  enabled?: boolean;
  keepPreviousData?: boolean;
  include_metadata?: boolean;
}) => {
  const { workspaceId } = useParams();
  const authContext = useAuth();

  return useQuery<Response<ListFolderEntitiesResponse["entities"], "entities", string>>({
    queryKey: [
      "folder-entities",
      workspaceId,
      folder_id,
      filter_types,
      search_query,
      semantic_search,
      semantic_search_threshold,
      semantic_search_top_k,
      flatten,
      include_metadata,
      include_previews,
      user_id,
      tags,
    ],
    queryFn: async () => {
      const url = new URL(ENDPOINTS.folder_entities(workspaceId || ""));

      if (filter_types) {
        filter_types.forEach(filter_type => {
          url.searchParams.append("filter_type", filter_type);
        });
      }

      if (include_metadata) {
        url.searchParams.set("include_metadata", "true");
      }

      if (include_previews) {
        url.searchParams.set("include_previews", "true");
      }

      if (folder_id !== undefined && folder_id !== null) {
        url.searchParams.set("folder_id", folder_id.toString());
      }

      if (search_query) {
        url.searchParams.set("search_query", search_query);
      }

      if (semantic_search) {
        url.searchParams.set("semantic_search", "true");
      }

      if (semantic_search_threshold !== undefined) {
        url.searchParams.set("semantic_search_threshold", semantic_search_threshold.toString());
      }

      if (semantic_search_top_k !== undefined) {
        url.searchParams.set("semantic_search_top_k", semantic_search_top_k.toString());
      }

      if (flatten) {
        url.searchParams.set("flatten", "true");
      }

      if (user_id) {
        url.searchParams.set("user_id", user_id.toString());
      }

      if (tags && tags.length > 0) {
        tags.forEach(tag => {
          url.searchParams.append("tags", tag);
        });
      }

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: jsonAuthHeaders(authContext?.userToken ?? ""),
      });
      const result = await handleResponse(response);
      return {
        ...result,
        entities: filterSupportedEntities(result.entities),
      };
    },
    enabled: enabled && !!authContext?.userToken && !!workspaceId,
    staleTime: 1000 * 30, // 30 seconds - shorter for better responsiveness
    refetchOnWindowFocus: false,
    refetchOnMount: "always", // Always re-check previews/folder state when navigating back to the registry.
    refetchOnReconnect: false,
    keepPreviousData,
    refetchInterval: false,
    refetchIntervalInBackground: false,
  });
};
